import type { ComponentType } from 'react';
import {
  FacebookIcon,
  GoogleBusinessIcon,
  InstagramIcon,
  LinkedInIcon,
  SendgridIcon,
  TwilioIcon,
} from '../PlatformIcon';
import { GBP_ENABLED, SOCIAL_ENABLED } from '../../lib/featureFlags';

export type ChannelStatus = 'connected' | 'pending' | 'disconnected';

export interface ConnectedChannelItem {
  id: string;
  label: string;
  status: ChannelStatus;
  detail?: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  onClick?: () => void;
  hidden?: boolean;
  /** Connected profile identity (shown in the Platforms sheet). */
  profileName?: string | null;
  profileHandle?: string | null;
}

interface ConnectedChannelsBarProps {
  channels: ConnectedChannelItem[];
}

/**
 * Quiet channel status row above the marketing calendar.
 */
export default function ConnectedChannelsBar({ channels }: ConnectedChannelsBarProps) {
  const visible = channels.filter((c) => !c.hidden);
  if (visible.length === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-x-1 gap-y-1.5"
      style={{
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', system-ui, sans-serif",
      }}
    >
      <span className="mr-1 text-[11px] font-medium text-gray-400 tracking-wide">
        Channels
      </span>
      {visible.map((channel) => {
        const Icon = channel.icon;
        const muted = channel.status === 'disconnected';
        const pending = channel.status === 'pending';
        return (
          <button
            key={channel.id}
            type="button"
            onClick={channel.onClick}
            title={
              channel.detail
                ? `${channel.label}: ${channel.detail}`
                : `${channel.label}: ${
                    channel.status === 'connected'
                      ? 'Connected'
                      : channel.status === 'pending'
                        ? 'Pending'
                        : 'Not connected'
                  }`
            }
            className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[12px] touch-manipulation transition-colors ${
              muted
                ? 'text-gray-400 hover:bg-black/[0.03] hover:text-gray-500'
                : pending
                  ? 'text-amber-700/80 hover:bg-amber-50/60'
                  : 'text-gray-600 hover:bg-black/[0.03] hover:text-gray-800'
            }`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <span
              className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                muted
                  ? 'bg-gray-300'
                  : pending
                    ? 'bg-amber-400'
                    : 'bg-emerald-500'
              }`}
              aria-hidden
            />
            <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
            <span className="font-medium">{channel.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function buildMarketingChannelItems(args: {
  sendgridStatus: ChannelStatus;
  sendgridDetail?: string;
  twilioStatus: ChannelStatus;
  twilioDetail?: string;
  gbpConnected: boolean;
  /** Every enabled Google location — one Platforms row each. */
  gbpLocations?: Array<{ id: string; name: string | null }>;
  facebookConnected: boolean;
  instagramConnected: boolean;
  linkedinConnected: boolean;
  /** Connected profile identity for the Platforms sheet. */
  gbpProfileName?: string | null;
  facebookProfile?: { name?: string | null; username?: string | null } | null;
  instagramProfile?: { name?: string | null; username?: string | null } | null;
  linkedinProfile?: { name?: string | null; username?: string | null } | null;
  onSendgrid: () => void;
  onTwilio: () => void;
  onSocial: () => void;
}): ConnectedChannelItem[] {
  return [
    {
      id: 'sendgrid',
      label: 'SendGrid',
      status: args.sendgridStatus,
      detail: args.sendgridDetail,
      icon: SendgridIcon,
      onClick: args.onSendgrid,
    },
    {
      id: 'twilio',
      label: 'Twilio',
      status: args.twilioStatus,
      detail: args.twilioDetail,
      icon: TwilioIcon,
      onClick: args.onTwilio,
    },
    ...(args.gbpLocations && args.gbpLocations.length > 0
      ? args.gbpLocations.map((loc) => ({
          id: `gbp:${loc.id}`,
          label: 'Google',
          status: 'connected' as ChannelStatus,
          icon: GoogleBusinessIcon,
          onClick: args.onSocial,
          hidden: !GBP_ENABLED,
          profileName: loc.name ?? null,
        }))
      : [
          {
            id: 'gbp',
            label: 'Google',
            status: (args.gbpConnected ? 'connected' : 'disconnected') as ChannelStatus,
            icon: GoogleBusinessIcon,
            onClick: args.onSocial,
            hidden: !GBP_ENABLED,
            profileName: args.gbpProfileName ?? null,
          },
        ]),
    {
      id: 'facebook',
      label: 'Facebook',
      status: args.facebookConnected ? 'connected' : 'disconnected',
      icon: FacebookIcon,
      onClick: args.onSocial,
      hidden: !SOCIAL_ENABLED,
      profileName: args.facebookProfile?.name ?? null,
      profileHandle: args.facebookProfile?.username ?? null,
    },
    {
      id: 'instagram',
      label: 'Instagram',
      status: args.instagramConnected ? 'connected' : 'disconnected',
      icon: InstagramIcon,
      onClick: args.onSocial,
      hidden: !SOCIAL_ENABLED,
      profileName: args.instagramProfile?.name ?? null,
      profileHandle: args.instagramProfile?.username ?? null,
    },
    {
      id: 'linkedin',
      label: 'LinkedIn',
      status: args.linkedinConnected ? 'connected' : 'disconnected',
      icon: LinkedInIcon,
      onClick: args.onSocial,
      hidden: !SOCIAL_ENABLED,
      profileName: args.linkedinProfile?.name ?? null,
      profileHandle: args.linkedinProfile?.username ?? null,
    },
  ];
}
